import { Alert, Button, EmptyState, LoadingState, useToast } from '@orbind/ui'
import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { ExplorerBody, useExplorer, useExplorerConfig } from '../shell/explorer.tsx'
import { categoriaDoKind, fraseDaNotificacao, metaDaCategoria } from '../features/notification/categorias.ts'
import {
  useMarcarLida,
  useMarcarNaoLida,
  useMarcarTodasLidas,
  useNotificacoes,
  type Notificacao,
} from '../features/notification/useNotificacoes.ts'
import './notifications.css'

/* ------------------------------------------------------------------ datas */

const RELATIVE_FMT = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' })
const DATE_FMT = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
const TIME_FMT = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' })

/**
 * "há 12 minutos", "ontem", "há 4 dias" ou a data cheia para o que é antigo.
 * Tudo com Intl em pt-BR — nenhuma string de data montada na mão.
 */
const relativeLabel = (iso: string, now: number) => {
  const date = new Date(iso)
  const minutes = Math.round((date.getTime() - now) / 60_000)
  const absolute = Math.abs(minutes)
  if (absolute < 60) return RELATIVE_FMT.format(minutes, 'minute')
  if (absolute < 60 * 24) return RELATIVE_FMT.format(Math.round(minutes / 60), 'hour')
  if (absolute < 60 * 24 * 7) return RELATIVE_FMT.format(Math.round(minutes / (60 * 24)), 'day')
  return `${DATE_FMT.format(date)} às ${TIME_FMT.format(date)}`
}

/** Data e hora cheias — o usuário pediu "data e hora" da atribuição, sem rodeio. */
const exactLabel = (iso: string) => {
  const date = new Date(iso)
  return `${DATE_FMT.format(date)} às ${TIME_FMT.format(date)}`
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

type GroupId = 'today' | 'week' | 'before'

const GROUP_LABEL: Record<GroupId, string> = {
  today: 'Hoje',
  week: 'Esta semana',
  before: 'Antes',
}

/** Hoje = mesmo dia; esta semana = últimos 7 dias; antes = o resto. */
const groupOf = (iso: string, now: number): GroupId => {
  const time = new Date(iso).getTime()
  if (time >= startOfDay(new Date(now))) return 'today'
  if (time >= now - 7 * 24 * 60 * 60_000) return 'week'
  return 'before'
}

/* ------------------------------------------------------------------ tela  */

/**
 * Central de notificações (SPEC-20260924-2111) — master-detail: a LISTA vive no
 * explorer do shell e o `<main>` é a região de leitura, com o cartão de
 * contexto e "Abrir o item". Sem nada selecionado o `<main>` fica no estado
 * ocioso.
 *
 * O dado é REAL: vem de `/api/notifications`, e o estado de leitura é gravado no
 * banco. O "não lida" nunca é comunicado apenas pelo ponto azul — a linha
 * carrega o rótulo "Não lida".
 */
export function NotificationsPage() {
  const navigate = useNavigate()
  const toast = useToast()

  // busca e chip são do explorer do shell (`shell/modules.ts`, módulo
  // `notifications`); a tela só LÊ e filtra. Chip vazio = nenhum chip escolhido.
  const { search, chip: chipDoShell } = useExplorer()
  const chip = chipDoShell || 'Todas'

  const consulta = useNotificacoes(true)
  const marcarLida = useMarcarLida()
  const marcarNaoLida = useMarcarNaoLida()
  const marcarTodas = useMarcarTodasLidas()

  const items = consulta.data?.itens ?? []
  const unreadCount = consulta.data?.naoLidas ?? 0

  const [selectedId, setSelectedId] = useState<string | null>(null)

  // "agora" congelado na montagem: se fosse recalculado a cada render, uma
  // notificação poderia pular de grupo no meio de uma interação
  const now = useMemo(() => Date.now(), [])

  useExplorerConfig({
    footer: `${items.length} ${items.length === 1 ? 'notificação' : 'notificações'} · ${unreadCount} não lidas`,
  })

  const marcarTudoComoLido = () =>
    marcarTodas.mutate(undefined, {
      onSuccess: () => toast.push({ tone: 'success', message: 'Todas as notificações foram marcadas como lidas.' }),
      onError: (e) =>
        toast.push({
          tone: 'danger',
          message: e instanceof Error ? e.message : 'Não foi possível marcar as notificações como lidas.',
        }),
    })

  const termo = search.trim().toLocaleLowerCase('pt-BR')
  const filtrosAtivos = chip !== 'Todas' || termo !== ''

  const visible = items.filter((item) => {
    if (chip !== 'Todas' && categoriaDoKind(item.kind) !== chip) return false
    if (termo === '') return true
    return [fraseDaNotificacao(item), item.code, item.titulo, item.autor, categoriaDoKind(item.kind)]
      .join(' ')
      .toLocaleLowerCase('pt-BR')
      .includes(termo)
  })

  const groups = useMemo(() => {
    const buckets: { id: GroupId; items: Notificacao[] }[] = [
      { id: 'today', items: [] },
      { id: 'week', items: [] },
      { id: 'before', items: [] },
    ]
    for (const item of visible) {
      const target = buckets.find((bucket) => bucket.id === groupOf(item.criadaEm, now))
      target?.items.push(item)
    }
    for (const bucket of buckets) {
      bucket.items.sort((a, b) => (a.criadaEm < b.criadaEm ? 1 : -1))
    }
    return buckets.filter((bucket) => bucket.items.length > 0)
  }, [visible, now])

  const selected = items.find((item) => item.id === selectedId) ?? null

  /** Selecionar também marca como lida. */
  const abrirNotificacao = (item: Notificacao) => {
    setSelectedId(item.id)
    if (!item.lida) marcarLida.mutate(item.id)
  }

  /**
   * Clicar leva AO ITEM — foi o pedido literal ("ao clicar na notificação abra
   * o item"). Selecionar a linha já a marcou como lida.
   */
  const abrirOItem = (item: Notificacao) => {
    // convite: leva ao dia do evento no Calendário (SPEC-20261027-2350-calendario-real)
    if (item.kind === 'calendar_invite') {
      if (!item.calendarEventId) {
        toast.push({ tone: 'warning', message: 'O evento deste convite não existe mais.' })
        return
      }
      navigate(`/calendar?${item.data ? `data=${item.data}&` : ''}evento=${item.calendarEventId}`)
      return
    }
    if (!item.workItemId) {
      toast.push({ tone: 'warning', message: 'O Item de Trabalho desta notificação não existe mais.' })
      return
    }
    navigate(`/work-items/${item.workItemId}`)
  }

  /* ------------------------------------------------------------ a lista -- */

  const lista: ReactNode = consulta.isPending ? (
    <div className="nf-listloading">
      <LoadingState rows={6} label="Carregando as suas notificações" />
    </div>
  ) : consulta.isError ? (
    <div className="nf-listempty">
      <Alert
        tone="danger"
        title="Não foi possível carregar as notificações"
        action={
          <Button size="sm" icon="fas fa-redo" onClick={() => void consulta.refetch()}>
            Tentar de novo
          </Button>
        }
      >
        {consulta.error instanceof Error ? consulta.error.message : 'Tente novamente em instantes.'}
      </Alert>
    </div>
  ) : groups.length === 0 ? (
    <div className="nf-listempty">
      <EmptyState
        icon={filtrosAtivos ? 'fas fa-filter' : 'far fa-bell'}
        title={filtrosAtivos ? 'Nenhuma notificação com este filtro' : 'Nenhuma notificação por aqui'}
        description={
          filtrosAtivos
            ? // a busca e o chip vivem no explorer do shell: a tela não tem como
              // limpá-los, então o texto aponta onde estão os controles
              'Nenhuma notificação corresponde à busca ou à categoria escolhida. Escolha “Todas” ou apague a busca acima.'
            : 'Quando alguém atribuir um Item de Trabalho a você, o aviso aparece aqui.'
        }
        action={
          <Button variant="primary" icon="fas fa-home" onClick={() => navigate('/home')}>
            Voltar para o Início
          </Button>
        }
      />
    </div>
  ) : (
    <>
      <div className="nf-listtop">
        <span className="nf-listtop__count">
          {unreadCount} {unreadCount === 1 ? 'não lida' : 'não lidas'}
        </span>
        <button
          type="button"
          className="nf-listtop__all"
          onClick={marcarTudoComoLido}
          disabled={unreadCount === 0 || marcarTodas.isPending}
        >
          Marcar todas como lidas
        </button>
      </div>

      {groups.map((group) => (
        <section key={group.id} className="nf-group" aria-label={GROUP_LABEL[group.id]}>
          <header className="nf-group__head">
            <span className="nf-group__label">{GROUP_LABEL[group.id]}</span>
            <span className="nf-group__count">
              {group.items.length} {group.items.length === 1 ? 'notificação' : 'notificações'}
            </span>
          </header>

          {group.items.map((item) => {
            const categoria = categoriaDoKind(item.kind)
            const meta = metaDaCategoria(categoria)
            const ativo = item.id === selectedId
            return (
              <button
                key={item.id}
                type="button"
                className={['nf-row', item.lida ? '' : 'nf-row--unread', ativo ? 'nf-row--sel' : '']
                  .filter(Boolean)
                  .join(' ')}
                aria-current={ativo ? 'true' : undefined}
                onClick={() => abrirNotificacao(item)}
              >
                <span className={`nf-icon nf-icon--${meta.tone}`} aria-hidden>
                  <i className={meta.icon} />
                </span>
                <span className="nf-row__body">
                  <span className="nf-row__text">{fraseDaNotificacao(item)}</span>
                  <span className="nf-row__meta">
                    <span className="nf-row__cat">{categoria}</span>
                    <span className="nf-row__ctx">{item.code || 'Item de Trabalho'}</span>
                    <span className="nf-row__time">· {relativeLabel(item.criadaEm, now)}</span>
                    {item.lida ? null : <span className="nf-row__unread">· Não lida</span>}
                  </span>
                </span>
                {item.lida ? null : <span className="nf-row__dot" aria-hidden />}
              </button>
            )
          })}
        </section>
      ))}
    </>
  )

  /* ------------------------------------------------------------ a leitura */

  return (
    <>
      <ExplorerBody>{lista}</ExplorerBody>

      <div className="nv">
        {consulta.isPending ? (
          <div className="nv-loading">
            <LoadingState variant="detail" label="Carregando a notificação" />
          </div>
        ) : selected === null ? (
          <div className="nv-idle">
            <div className="nv-idle__icon" aria-hidden>
              <i className="fas fa-bell" />
            </div>
            <h2 className="nv-idle__title">Central de notificações</h2>
            <p className="nv-idle__text">
              As atribuições de Itens de Trabalho aparecem aqui. Selecione uma notificação para ver o contexto e abrir o
              item sem perder o fio.
            </p>
            {/* os avisos também vão por e-mail (SPEC-20261103-1626); a escolha fica no perfil */}
            <Button variant="ghost" icon="far fa-envelope" onClick={() => navigate('/profile')}>
              Escolher o que chega por e-mail
            </Button>
          </div>
        ) : (
          <div className="nv-stack">
            <article className="nv-card">
              <header className="nv-card__head">
                <span
                  className={`nf-icon nf-icon--${metaDaCategoria(categoriaDoKind(selected.kind)).tone}`}
                  aria-hidden
                >
                  <i className={metaDaCategoria(categoriaDoKind(selected.kind)).icon} />
                </span>
                <span className="nv-cat">{categoriaDoKind(selected.kind)}</span>
                <span className="nv-grow" />
                {/* relativo para o relance, exato no title — o usuário pediu data e hora */}
                <span className="nv-time" title={exactLabel(selected.criadaEm)}>
                  {relativeLabel(selected.criadaEm, now)}
                </span>
              </header>

              <p className="nv-text">{fraseDaNotificacao(selected)}</p>
              <p className="nv-actor">
                {selected.kind === 'calendar_invite' ? 'Quem convidou' : 'Quem atribuiu'}: {selected.autor} ·{' '}
                {exactLabel(selected.criadaEm)}
              </p>

              <button
                type="button"
                className="nv-ctx"
                title={`Abrir ${selected.code} — ${selected.titulo}`}
                onClick={() => abrirOItem(selected)}
              >
                <span className="nv-ctx__code">
                  {selected.kind === 'calendar_invite' ? (
                    <>
                      <i className="far fa-calendar-check" aria-hidden /> Evento
                      {selected.data ? ` · ${selected.data.split('-').reverse().join('/')}` : ''}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-tasks" aria-hidden /> {selected.code || 'Item de Trabalho'}
                    </>
                  )}
                </span>
                <span className="nv-ctx__title">{selected.titulo}</span>
                <i className="fas fa-arrow-right" aria-hidden />
              </button>

              <div className="nv-actions">
                <Button variant="primary" icon="fas fa-arrow-right" onClick={() => abrirOItem(selected)}>
                  {selected.kind === 'calendar_invite' ? 'Abrir no calendário' : 'Abrir o item'}
                </Button>
                <Button
                  variant="ghost"
                  icon={selected.lida ? 'far fa-envelope' : 'fas fa-check'}
                  disabled={marcarLida.isPending || marcarNaoLida.isPending}
                  onClick={() => (selected.lida ? marcarNaoLida.mutate(selected.id) : marcarLida.mutate(selected.id))}
                >
                  {selected.lida ? 'Marcar como não lida' : 'Marcar como lida'}
                </Button>
              </div>
            </article>
          </div>
        )}
      </div>
    </>
  )
}
